/**
 * AIProviderFilter.jsx
 *
 * AI-guided provider filtering workflow that produces a Tag/Label group.
 *
 * Usage:
 *   <AIProviderFilter
 *     allProviders={[...]}       // full loaded provider list from Redux
 *     specialtyList={[...]}      // flat string[] of all specialty names in system
 *     existingNamespaces={[...]} // TagNamespaces[] with providers_drop_down=true
 *     onClose={() => {}}         // called when user dismisses
 *   />
 *
 * Props shape:
 *   Provider: { id, first_name, last_name, specialty, county, state, npi }
 *   TagNamespace: { id, name, tags: [{ id, name }] }
 *
 * Backend calls (all POST /AI):
 *   { type: 'interpret-filter', originalPrompt, followUp, currentCriteria, specialtyList }
 *   { type: 'save-provider-labels', mode, namespaceId, namespaceName, tagId, tagName, providerIds }
 */

import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withRouter } from 'utils';
import axiosInstance from 'utils/axiosUtil';
import {
  Box, Button, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, Divider, IconButton, InputAdornment,
  MenuItem, Paper, Select, Stack, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, Tooltip, Typography,
  FormControl, InputLabel, Alert,
  TablePagination,
} from '@mui/material';
import AutoFixHighIcon    from '@mui/icons-material/AutoFixHigh';
import CloseIcon          from '@mui/icons-material/Close';
import SendIcon           from '@mui/icons-material/Send';
import DeleteOutlineIcon  from '@mui/icons-material/DeleteOutline';
import CheckCircleIcon    from '@mui/icons-material/CheckCircle';
import LabelIcon          from '@mui/icons-material/Label';
import FilterListIcon     from '@mui/icons-material/FilterList';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const NAMESPACE_NAME_MAX = 20;
const PAGE_SIZE = 25;
const TAG_NAME_MAX       = 50;
// Keep in sync with MAX_LABEL_RESULTS in doctivity-rest aiService.js — the backend caps how
// many providers/orgs a filter returns/saves; when the match count exceeds this we warn the user.
const MAX_LABEL_RESULTS = 5000;

// Example filter prompts shown when the dialog opens without a filter (provider/org variants).
const FILTER_TIPS = {
  providers: [
    'Cardiologists in Chicago, IL',
    'Providers in ZIP codes 60601, 60602, 60603',
    'Affiliated family medicine providers in Cook County',
    'Providers at WellSpan organizations',
  ],
  organizations: [
    'Hospitals in Pennsylvania',
    'Urgent care organizations in ZIP 17201',
    'Affiliated organizations in Dauphin County',
    'Organizations with "WellSpan" in the name',
  ],
};

const EMPTY_CRITERIA = {
  counties:           [],
  states:             [],
  specialtyKeywords:  [],
  excludeProviderIds: [],
  summary:            '',
};

const SAVE_MODES = {
  NEW_NAMESPACE: 'new_namespace',
  NEW_TAG:       'new_tag',
  EXISTING_TAG:  'existing_tag',
};

// ---------------------------------------------------------------------------
// Pure helpers
// ---------------------------------------------------------------------------

/** Client-side filter — no API call */
function applyFilters(allProviders, criteria) {
  if (!allProviders) return [];
  return allProviders.filter((p) => {
    if (criteria.excludeProviderIds.includes(p.id)) return false;

    if (criteria.counties.length > 0) {
      const match = criteria.counties.some(
        (c) => (p.county || '').toLowerCase() === c.toLowerCase(),
      );
      if (!match) return false;
    }

    if (criteria.states.length > 0) {
      if (!criteria.states.includes(p.state)) return false;
    }

    if (criteria.specialtyKeywords.length > 0) {
      const match = criteria.specialtyKeywords.some((kw) =>
        (p.specialty || '').toLowerCase().includes(kw.toLowerCase()),
      );
      if (!match) return false;
    }

    return true;
  });
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------


// Extract display string from a where value (handles $in arrays, plain strings, etc.)
function displayVal(v) {
  if (!v) return '';
  if (typeof v === 'string') return v;
  if (typeof v === 'number') return String(v);
  if (Array.isArray(v)) return v.filter(Boolean).join(', ');
  if (v['$eq'])   return String(v['$eq']);
  if (v['$in'])   return [].concat(v['$in']).filter(Boolean).join(', ');
  if (v['$like']) return v['$like'].replace(/%/g, '');
  if (v['$iLike']) return v['$iLike'].replace(/%/g, '');
  // Handle nested Op objects — take first defined value
  try {
    const first = Object.values(v).find(x => x !== null && x !== undefined);
    return first ? String(first) : '';
  } catch {
    return '';
  }
}

// Derive displayable filter chips from a Sequelize query object
// Extract first readable value from a $or array of {field: {$like: val}} conditions
function extractOrVal(orArr) {
  if (!Array.isArray(orArr)) return null;
  for (const cond of orArr) {
    if (!cond || typeof cond !== 'object') continue;
    for (const fieldVal of Object.values(cond)) {
      if (fieldVal === null || fieldVal === undefined) continue;
      const v = displayVal(fieldVal);
      if (v) return v;
    }
  }
  return null;
}

function getQueryChips(query) {
  if (!query) return [];
  const chips = [];

  // Seed-from-label-group (UC2) — structural source filter, shown distinctly and not removable
  if (query.sourceLabel) {
    const d = query.sourceLabel.display;
    chips.push({ label: d ? `From: ${d}` : 'From existing label group', key: 'sourceLabel', color: 'info' });
  }

  // Top-level where clauses
  const where = query.where || {};
  if (where.first_name)    chips.push({ label: `First: ${displayVal(where.first_name)}`, key: 'first_name' });
  if (where.last_name)     chips.push({ label: `Last: ${displayVal(where.last_name)}`, key: 'last_name' });
  if (where.npi)           chips.push({ label: `NPI: ${displayVal(where.npi)}`, key: 'npi' });
  if (where.facility_type) chips.push({ label: `Type: ${displayVal(where.facility_type)}`, key: 'facility_type' });
  if (where.name)          chips.push({ label: `Name: ${displayVal(where.name)}`, key: 'name' });

  // Include-level where clauses
  (query.include || []).forEach((inc) => {
    const w = inc.where || {};
    if (inc.association === 'location' || inc.association === 'primary_location') {
      if (w.state)         chips.push({ label: `State: ${displayVal(w.state)}`, key: 'location.state' });
      else if (w.state?.$in) chips.push({ label: `State: ${w.state.$in.join(', ')}`, key: 'location.state' });
      if (w.city)          chips.push({ label: `City: ${displayVal(w.city)}`, key: 'location.city' });
      if (w.county_name)   chips.push({ label: `County: ${displayVal(w.county_name)}`, key: 'location.county_name' });
    }
    if (inc.association === 'taxonomy') {
      // Direct field match
      if (w.doctivity_name) chips.push({ label: `Specialty: ${displayVal(w.doctivity_name)}`, key: 'taxonomy.doctivity_name' });
      else if (w.specialization) chips.push({ label: `Specialty: ${displayVal(w.specialization)}`, key: 'taxonomy.specialization' });
      else if (w.classification) chips.push({ label: `Specialty: ${displayVal(w.classification)}`, key: 'taxonomy.classification' });
      // $or across all three taxonomy fields (preferred pattern)
      else if (w['$or']) {
        const v = extractOrVal(w['$or']);
        if (v) chips.push({ label: `Specialty: ${v}`, key: 'taxonomy.$or' });
      }
    }
    if (inc.association === 'is_affiliated' && inc.required) {
      chips.push({ label: 'Affiliated only', key: 'affiliated' });
    }
  });

  // Top-level $or (some queries put specialty filter here)
  if (where['$or']) {
    const v = extractOrVal(where['$or']);
    if (v) chips.push({ label: `Specialty: ${v}`, key: 'where.$or' });
  }

  return chips;
}

/** Chips row representing the active filter criteria */
function CriteriaChips({ criteria, onRemoveCounty, onRemoveState, onRemoveCity, onRemoveKeyword, onClearAll }) {
  const hasAny =
    criteria.counties.length > 0 ||
    criteria.states.length > 0 ||
    (criteria.cities || []).length > 0 ||
    criteria.specialtyKeywords.length > 0;

  if (!hasAny) return null;

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, alignItems: 'center', mb: 1.5 }}>
      <FilterListIcon sx={{ fontSize: 16, color: 'text.secondary', mr: 0.5 }} />

      {criteria.counties.map((c) => (
        <Chip
          key={`county-${c}`}
          label={`County: ${c}`}
          size="small"
          color="primary"
          variant="outlined"
                  />
      ))}

      {criteria.states.map((s) => (
        <Chip
          key={`state-${s}`}
          label={`State: ${s}`}
          size="small"
          color="primary"
          variant="outlined"
                  />
      ))}

      {(criteria.cities || []).map((c) => (
        <Chip key={`city-${c}`} label={`City: ${c}`} size="small" color="primary" variant="outlined" />
      ))}
      {criteria.specialtyKeywords.map((kw) => (
        <Chip
          key={`kw-${kw}`}
          label={kw}
          size="small"
          color="secondary"
          variant="outlined"
                  />
      ))}

      {hasAny && (
        <Chip
          label="Clear all"
          size="small"
          variant="outlined"
          color="error"
          onClick={onClearAll}
        />
      )}
    </Box>
  );
}


// Get a value from a nested object using dot notation path with optional fallbacks
function getColValue(row, path, fallbacks) {
  const get = (obj, p) => p.split('.').reduce((o, k) => (o && o[k] != null ? o[k] : null), obj);
  const val = get(row, path);
  if (val) return val;
  if (fallbacks) {
    for (const fb of fallbacks) {
      const v = get(row, fb);
      if (v) return v;
    }
  }
  return null;
}

/** Single provider row with exclude button */
function ProviderRow({ provider, onExclude, columns, entityType }) {
  const isOrgRow = entityType === 'organizations'
    || (columns?.some(c => c.key === 'name') && !provider.first_name && !provider.last_name)
    || provider.facility_type !== undefined;
  return (
    <TableRow hover>
      {!isOrgRow && <TableCell sx={{ py: 0.75 }}>{provider.last_name}, {provider.first_name}</TableCell>}
      {(columns || []).map((col) => {
        const val = getColValue(provider, col.path, col.fallbacks);
        const display = val == null ? '—'
          : col.key === 'captured_claims_count' ? Number(val).toLocaleString()
          : col.key === 'total_claims' ? Number(val).toLocaleString()
          : col.key === 'total_patients' ? Number(val).toLocaleString()
          : (col.key === 'affiliated' || col.format === 'boolean') ? (val ? '✓' : '—')
          : val;
        return <TableCell key={col.key} sx={{ py: 0.75 }}>{display}</TableCell>;
      })}
      <TableCell sx={{ py: 0.75 }} align="right">
        <Tooltip title="Exclude this provider">
          <IconButton size="small" onClick={() => onExclude(provider.id)}>
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </TableCell>
    </TableRow>
  );
}

/** Save dialog — handles all 3 modes */
function SaveDialog({ open, onClose, onSave, existingNamespaces, isSaving }) {
  const [mode,          setMode]          = useState(SAVE_MODES.NEW_NAMESPACE);
  const [namespaceName, setNamespaceName] = useState('');
  const [tagName,       setTagName]       = useState('');
  const [namespaceId,   setNamespaceId]   = useState('');
  const [tagId,         setTagId]         = useState('');

  const selectedNs = existingNamespaces.find((ns) => ns.id === namespaceId);
  const availableTags = selectedNs?.tags || [];

  const isValid = useMemo(() => {
    if (mode === SAVE_MODES.NEW_NAMESPACE) {
      return namespaceName.trim().length > 0 && tagName.trim().length > 0;
    }
    if (mode === SAVE_MODES.NEW_TAG) {
      return namespaceId && tagName.trim().length > 0;
    }
    if (mode === SAVE_MODES.EXISTING_TAG) {
      return namespaceId && tagId;
    }
    return false;
  }, [mode, namespaceName, tagName, namespaceId, tagId]);

  const handleSave = () => {
    onSave({ mode, namespaceName: namespaceName.trim(), tagName: tagName.trim(), namespaceId, tagId });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <LabelIcon color="primary" />
        Create Label Group
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={2.5}>

          <FormControl fullWidth size="small">
            <InputLabel>Action</InputLabel>
            <Select
              value={mode}
              label="Action"
              onChange={(e) => { setMode(e.target.value); setNamespaceId(''); setTagId(''); }}
            >
              <MenuItem value={SAVE_MODES.NEW_NAMESPACE}>Create new label group + label</MenuItem>
              <MenuItem value={SAVE_MODES.NEW_TAG}>Add new label to existing group</MenuItem>
              <MenuItem value={SAVE_MODES.EXISTING_TAG}>Add to existing label (no duplicates)</MenuItem>
            </Select>
          </FormControl>

          {/* ── NEW NAMESPACE ── */}
          {mode === SAVE_MODES.NEW_NAMESPACE && (
            <>
              <TextField
                label="Label Group Name"
                size="small"
                fullWidth
                value={namespaceName}
                onChange={(e) => setNamespaceName(e.target.value.slice(0, NAMESPACE_NAME_MAX))}
                helperText={`${namespaceName.length}/${NAMESPACE_NAME_MAX} characters`}
                inputProps={{ maxLength: NAMESPACE_NAME_MAX }}
              />
              <TextField
                label="Label Name"
                size="small"
                fullWidth
                value={tagName}
                onChange={(e) => setTagName(e.target.value.slice(0, TAG_NAME_MAX))}
                helperText={`${tagName.length}/${TAG_NAME_MAX} characters`}
                inputProps={{ maxLength: TAG_NAME_MAX }}
              />
            </>
          )}

          {/* ── NEW TAG in existing NS ── */}
          {mode === SAVE_MODES.NEW_TAG && (
            <>
              <FormControl fullWidth size="small">
                <InputLabel>Label Group</InputLabel>
                <Select
                  value={namespaceId}
                  label="Label Group"
                  onChange={(e) => setNamespaceId(e.target.value)}
                >
                  {existingNamespaces.map((ns) => (
                    <MenuItem key={ns.id} value={ns.id}>{ns.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="New Label Name"
                size="small"
                fullWidth
                value={tagName}
                onChange={(e) => setTagName(e.target.value.slice(0, TAG_NAME_MAX))}
                helperText={`${tagName.length}/${TAG_NAME_MAX} characters`}
                inputProps={{ maxLength: TAG_NAME_MAX }}
              />
            </>
          )}

          {/* ── EXISTING TAG ── */}
          {mode === SAVE_MODES.EXISTING_TAG && (
            <>
              <FormControl fullWidth size="small">
                <InputLabel>Label Group</InputLabel>
                <Select
                  value={namespaceId}
                  label="Label Group"
                  onChange={(e) => { setNamespaceId(e.target.value); setTagId(''); }}
                >
                  {existingNamespaces.map((ns) => (
                    <MenuItem key={ns.id} value={ns.id}>{ns.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              {namespaceId && (
                <FormControl fullWidth size="small">
                  <InputLabel>Label</InputLabel>
                  <Select
                    value={tagId}
                    label="Label"
                    onChange={(e) => setTagId(e.target.value)}
                  >
                    {availableTags.map((t) => (
                      <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              <Alert severity="info" sx={{ py: 0.5 }}>
                Providers already in this label will be skipped automatically.
              </Alert>
            </>
          )}

        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={isSaving}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!isValid || isSaving}
          startIcon={isSaving ? <CircularProgress size={16} /> : <CheckCircleIcon />}
        >
          {isSaving ? 'Saving…' : 'Save Label Group'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

function AIProviderFilter({ allProviders, specialtyList, existingNamespaces, onClose, selectedClient, user, aiLabelFilter, router }) {

  // ── filter state ──────────────────────────────────────────────────────────
  const [originalPrompt, setOriginalPrompt] = useState('');
  const [followUpInput,  setFollowUpInput]  = useState('');
  const [criteria,          setCriteria]          = useState(EMPTY_CRITERIA);
  const [isInterpreting,    setIsInterpreting]    = useState(false);
  const [interpretError,    setInterpretError]    = useState(null);
  const [hasResults,        setHasResults]        = useState(false);
  const [criteriaRows,      setCriteriaRows]      = useState([]);
  const [criteriaCount,     setCriteriaCount]     = useState(0);
  const [criteriaPage,      setCriteriaPage]      = useState(0);
  const [aiLimit,           setAiLimit]           = useState(null);
  const [isCriteriaFetching,setIsCriteriaFetching]= useState(false);
  const [currentQuery,      setCurrentQuery]      = useState(null);
  const [columns,           setColumns]           = useState([]);
  const [needsFilter,       setNeedsFilter]       = useState(false);

  // ── save state ────────────────────────────────────────────────────────────
  const [saveOpen,  setSaveOpen]  = useState(false);
  const [isSaving,  setIsSaving]  = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [savedMsg,  setSavedMsg]  = useState(null);

  const followUpRef      = useRef(null);
  const currentQueryRef  = useRef(null); // always-current copy for pagination
  const pendingFetchRef  = useRef(false); // guard against duplicate fetches

  const fetchCriteriaProviders = useCallback(async (activeCriteria, page = 0) => {
    if (!activeCriteria) return;
    setIsCriteriaFetching(true);
    try {
      const resp = await axiosInstance.post('/AI', {
        type: 'queryProvidersByCriteria', criteria: activeCriteria,
        limit: PAGE_SIZE, offset: page * PAGE_SIZE, client_id: selectedClient,
      });
      setCriteriaRows(resp.data?.rows || []);
      setCriteriaCount(resp.data?.count || 0);
      setCriteriaPage(page);
    } catch (err) { console.error('[AIProviderFilter] fetch error:', err); }
    finally { setIsCriteriaFetching(false); }
  }, [selectedClient]);

  const callBuildAndExecute = useCallback(async (prompt, followUp, existingQuery, page = 0) => {
    if (!prompt && !followUp && !existingQuery) {
      console.warn('[callBuildAndExecute] nothing to query');
      return;
    }
    if (pendingFetchRef.current) {
      console.warn('[callBuildAndExecute] skipping duplicate request');
      return;
    }
    pendingFetchRef.current = true;
    setIsCriteriaFetching(true);
    try {
      // Always use buildAndExecuteQuery so HAVING/subquery logic is re-applied on pagination
      const resp = await axiosInstance.post('/AI', {
        type: 'buildAndExecuteQuery',
        prompt: prompt || undefined, followUp: followUp || undefined,
        currentQuery: existingQuery || undefined,
        // Tell the backend which entity this dialog is filtering so a fresh query (e.g. clicking
        // an org sample on the "needs filter" screen) builds an Organizations query, not Providers.
        defaultModel: aiLabelFilter?.entityType === 'organizations' ? 'Organizations' : 'Providers',
        limit: aiLimit || PAGE_SIZE, offset: page * (aiLimit || PAGE_SIZE), client_id: selectedClient,
      });
      if (resp.data?.error) { setInterpretError(resp.data.error); }
      else {
        setCriteriaRows(resp.data?.rows || []);
        setCriteriaCount(resp.data?.count || 0);
        setCurrentQuery(resp.data?.query || null);
        currentQueryRef.current = resp.data?.query || null;
        // Keep needsFilter in sync with the backend so the no-rows tips message is correct:
        // needsFilter=true → "Add a filter"; false (a real filter ran) → "No matches".
        setNeedsFilter(!!resp.data?.needsFilter);
        if (resp.data?.aiLimit) setAiLimit(resp.data.aiLimit); else setAiLimit(null);
        if (resp.data?.columns?.length > 0) setColumns(resp.data.columns);
        setCriteriaPage(page);
        setHasResults(true);
      }
    } catch (err) { console.error('[AIProviderFilter] query error:', err); }
    finally { setIsCriteriaFetching(false); pendingFetchRef.current = false; }
  }, [selectedClient, aiLabelFilter?.entityType]);

  // ── derived filtered list ─────────────────────────────────────────────────
  const filteredProviders = useMemo(
    () => applyFilters(allProviders, criteria),
    [allProviders, criteria],
  );

  // ── focus follow-up input after first result ──────────────────────────────
  useEffect(() => {
    if (hasResults) followUpRef.current?.focus();
  }, [hasResults]);

  // ── reset local state when dialog opens fresh ────────────────────────────────
  const prevOpenRef = useRef(false);
  useEffect(() => {
    const justOpened = aiLabelFilter?.open && !prevOpenRef.current;
    prevOpenRef.current = !!aiLabelFilter?.open;
    if (justOpened) {
      // Full reset of local state so previous session data doesn't bleed through
      setCriteria(EMPTY_CRITERIA);
      setCriteriaRows([]);
      setCriteriaCount(0);
      setCriteriaPage(0);
      setHasResults(false);
      setOriginalPrompt('');
      setFollowUpInput('');
      setSavedMsg(null);
      setSaveError(null);
    }
  }, [aiLabelFilter?.open]);

  // Listen for audio filter fetch events (audio path doesn't go through callBuildAndExecute)
  useEffect(() => {
    const handler = (e) => setIsCriteriaFetching(e.detail?.fetching ?? false);
    window.addEventListener('ai-filter-fetching', handler);
    return () => window.removeEventListener('ai-filter-fetching', handler);
  }, []);
  // count + first id ensures key changes whenever new results arrive
  // Include query structure so column changes (add/remove) also trigger re-render
  const reduxQueryKey = `${aiLabelFilter?.queryCount}:${aiLabelFilter?.queryRows?.[0]?.id}:${aiLabelFilter?.queryRows?.[aiLabelFilter?.queryRows?.length-1]?.id}:${JSON.stringify(aiLabelFilter?.columns?.map(c=>c.key))}:${JSON.stringify(aiLabelFilter?.currentQuery?.attributes)}:${JSON.stringify(aiLabelFilter?.currentQuery?.include?.map(i=>i.where))}:${JSON.stringify(aiLabelFilter?.currentQuery?.where)}:${aiLabelFilter?.needsFilter}`;
  const reduxCriteriaKey = JSON.stringify(aiLabelFilter?.criteria);

  // Column visibility driven by columns array from AI
  const effectiveColumns = columns;
  useEffect(() => {
    if (!aiLabelFilter?.open) return;
    if (aiLabelFilter.queryRows !== undefined) {
      setCriteriaRows(aiLabelFilter.queryRows || []);
      setCriteriaCount(aiLabelFilter.queryCount || 0);
      setNeedsFilter(!!aiLabelFilter.needsFilter);
      // A refine that drops every filter comes back needsFilter — show tips, not an empty table.
      if (aiLabelFilter.needsFilter) setHasResults(false);
      const incomingQuery = aiLabelFilter.currentQuery || null;
      setCurrentQuery(incomingQuery);
      currentQueryRef.current = incomingQuery;
      console.log('[AiProviderFilter] syncing columns from Redux:', aiLabelFilter.columns?.length, aiLabelFilter.columns);
      if (aiLabelFilter.queryRows?.[0]) console.log('[AiProviderFilter] first row is_affiliated:', JSON.stringify(aiLabelFilter.queryRows[0].is_affiliated), 'email:', aiLabelFilter.queryRows[0].email);
      setColumns(aiLabelFilter.columns || []);
      // Only show results UI if we actually have rows or a non-zero count
      if ((aiLabelFilter.queryRows?.length > 0) || (aiLabelFilter.queryCount > 0)) {
        setHasResults(true);
      }
    } else if (aiLabelFilter.criteria) {
      setCriteria(aiLabelFilter.criteria);
      setHasResults(true);
      fetchCriteriaProviders(aiLabelFilter.criteria, 0);
    }
    if (aiLabelFilter.initialPrompt && !originalPrompt) setOriginalPrompt(aiLabelFilter.initialPrompt);
  }, [reduxQueryKey, reduxCriteriaKey, aiLabelFilter?.open]);

  useEffect(() => {
    if (aiLabelFilter?.saveTrigger) {
      if (aiLabelFilter?.tagId) handleQuickSave();
      else setSaveOpen(true);
    }
  }, [aiLabelFilter?.saveTrigger]);

  // ── interpret: calls /AI with type='interpret-filter' ────────────────────
  const callInterpret = useCallback(async (prompt, followUp, currentCriteria) => {
    setIsInterpreting(true);
    setInterpretError(null);
    try {
      const resp = await axiosInstance.post('/AI', {
        type:           'interpret-filter',
        originalPrompt: prompt,
        followUp:       followUp || null,
        currentCriteria,
        specialtyList,
        client_id:      selectedClient,
      });

      if (resp.data?.criteria) {
        // Preserve client-managed excludeProviderIds — never let AI change them
        const merged = {
          ...resp.data.criteria,
          excludeProviderIds: currentCriteria.excludeProviderIds,
        };
        setCriteria(merged);
        setHasResults(true);
      } else {
        setInterpretError(resp.data?.error || 'No criteria returned.');
      }
    } catch (err) {
      console.error('[AIProviderFilter] interpret error:', err);
      setInterpretError('Something went wrong. Please try again.');
    } finally {
      setIsInterpreting(false);
    }
  }, [specialtyList, selectedClient]);

  // ── handlers ──────────────────────────────────────────────────────────────

  const handleInitialSubmit = () => {
    if (!originalPrompt.trim()) return;
    callBuildAndExecute(originalPrompt, null, null, 0);
  };

  const handleFollowUp = () => {
    if (!followUpInput.trim()) return;
    callBuildAndExecute(null, followUpInput.trim(), currentQuery, 0);
    setFollowUpInput('');
  };

  const handleExclude = useCallback((providerId) => {
    setCriteria((prev) => ({
      ...prev,
      excludeProviderIds: [...prev.excludeProviderIds, providerId],
    }));
  }, []);

  const handleRemoveCounty = (county) => {
    setCriteria((prev) => ({
      ...prev,
      counties: prev.counties.filter((c) => c !== county),
    }));
  };

  const handleRemoveState = (state) => {
    setCriteria((prev) => ({ ...prev, states: prev.states.filter((s) => s !== state) }));
  };

  const handleRemoveCity = (city) => {
    const next = { ...criteria, cities: (criteria.cities || []).filter((c) => c !== city) };
    setCriteria(next);
    fetchCriteriaProviders(next, 0);
  };

  const handleQuickSave = async () => {
    const lf = aiLabelFilter;
    if (!lf?.tagId) { console.warn('[handleQuickSave] no tagId'); return; }
    await handleSave({ mode: 'existing_tag', namespaceName: lf.namespaceName, tagName: lf.tagName, namespaceId: lf.namespaceId, tagId: lf.tagId });
  };

  // Removing a specialty keyword needs AI re-expansion — call interpret again
  const handleRemoveKeyword = (kw) => {
    const next = { ...criteria, specialtyKeywords: criteria.specialtyKeywords.filter((k) => k !== kw) };
    setCriteria(next);
    if (next.specialtyKeywords.length > 0) {
      callInterpret(originalPrompt, `Remove "${kw}" from specialty filter`, next);
    }
  };

  const handleClearAll = () => {
    setCriteria(EMPTY_CRITERIA);
    setHasResults(false);
    setOriginalPrompt('');
    setFollowUpInput('');
    setSavedMsg(null);
    setSaveError(null);
    setCriteriaRows([]);
    setCriteriaCount(0);
    setCurrentQuery(null);
  };

  // ── save ──────────────────────────────────────────────────────────────────

  const handleSave = async ({ mode, namespaceName, tagName, namespaceId, tagId }) => {
    if (criteriaRows.length === 0 && criteriaCount === 0) {
      setSaveError('No providers match the current filter. Refine your criteria before saving.');
      return;
    }
    setIsSaving(true);
    setSaveError(null);
    try {
      const isOrgs = aiLabelFilter?.entityType === 'organizations';
      const resp = await axiosInstance.post('/AI', {
        type: isOrgs ? 'saveOrgLabels' : 'saveProviderLabels',
        mode,
        namespaceName,
        tagName,
        namespaceId,
        tagId,
        currentQuery:  currentQuery || undefined,
        queryEngine:   currentQuery ? true : undefined,
        providerIds:   !isOrgs && !currentQuery ? criteriaRows.map(r => r.id) : undefined,
        orgIds:        isOrgs  && !currentQuery ? criteriaRows.map(r => r.id) : undefined,
        client_id: selectedClient,
      });

      if (resp.data?.success) {
        setSaveOpen(false);
        const savedTagId = resp.data.tag_id || aiLabelFilter?.tagId || tagId;
        setSavedMsg(`Saved — ${resp.data.count || criteriaCount} added to label.`);
        if (onClose) onClose();
        // Soft client-side navigation + a refresh event instead of a full page reload, so an
        // active audio/voice session survives the save. The labels page (TagDetailsPage /
        // OrganizationsByTag) listens for 'refresh-provider-tags' and refetches its list —
        // necessary because soft-navigating to the SAME tagId won't re-trigger its fetch.
        if (savedTagId) {
          setTimeout(() => {
            if (router?.navigate) router.navigate(`/labels/${savedTagId}`);
            else window.location.href = `/labels/${savedTagId}`;   // fallback if router unavailable
            window.dispatchEvent(new Event('refresh-provider-tags'));
          }, 150);
        }
      } else {
        setSaveError(resp.data?.error || 'Save failed.');
      }
    } catch (err) {
      console.error('[AIProviderFilter] save error:', err);
      setSaveError('Save failed. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  // ── keyboard shortcuts ────────────────────────────────────────────────────

  const handlePromptKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleInitialSubmit(); }
  };

  const handleFollowUpKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleFollowUp(); }
  };

  // ── render ────────────────────────────────────────────────────────────────

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', gap: 2 }}>

      {/* ── Header ── */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AutoFixHighIcon color="primary" />
          <Typography variant="h6" fontWeight={600}>
            {aiLabelFilter?.entityType === 'organizations' ? 'AI Organization Filter' : 'AI Provider Filter'}
          </Typography>
        </Box>
        {onClose && (
          <IconButton size="small" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </Box>



      {/* ── Error states ── */}
      {interpretError && (
        <Alert severity="error" onClose={() => setInterpretError(null)}>{interpretError}</Alert>
      )}
      {saveError && (
        <Alert severity="error" onClose={() => setSaveError(null)}>{saveError}</Alert>
      )}
      {savedMsg && (
        <Alert severity="success" icon={<CheckCircleIcon />} onClose={() => setSavedMsg(null)}>
          {savedMsg}
        </Alert>
      )}

      {/* ── First-run "thinking" indicator ── the overlay below only renders once
           hasResults is true, so without this the very first launch/refine shows nothing
           while Key builds the query (~3s). ── */}
      {!hasResults && (isInterpreting || isCriteriaFetching) && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 1, py: 3, justifyContent: 'center' }}>
          <CircularProgress size={22} thickness={4} />
          <Typography variant="body2" color="text.secondary" fontWeight={500}>
            Key is thinking…
          </Typography>
        </Box>
      )}

      {/* ── No-rows state ── show tips/examples. Covers BOTH "no filter applied yet" AND "a filter
           ran but matched 0 results" — otherwise a 0-result filter (e.g. a misspelled city) shows
           a blank dialog. ── */}
      {criteriaRows.length === 0 && !(isInterpreting || isCriteriaFetching) && (() => {
        const entityType = aiLabelFilter?.entityType === 'organizations' ? 'organizations' : 'providers';
        const tips = FILTER_TIPS[entityType] || FILTER_TIPS.providers;
        const noMatches = !needsFilter; // a real filter ran but matched nothing
        return (
          <Box sx={{ px: 2, py: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, textAlign: 'center' }}>
            <FilterListIcon sx={{ fontSize: 42, color: 'primary.main', opacity: 0.8 }} />
            <Typography variant="subtitle1" fontWeight={600}>
              {noMatches ? `No ${entityType} matched your filter` : 'Add a filter to get started'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 480 }}>
              {noMatches
                ? `Nothing matched the current filter. Try a different one — check the spelling, broaden it, or pick an example below.`
                : `Tell Key how to narrow the ${entityType} — by specialty, geography (city, state, ZIP, county), organization, or affiliation. The full list isn't shown until you apply a filter.`}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, justifyContent: 'center', maxWidth: 580 }}>
              {tips.map((ex) => (
                <Chip key={ex} label={ex} size="small" variant="outlined" color="primary"
                  onClick={() => { setFollowUpInput(ex); callBuildAndExecute(ex, null, null, 0); }} />
              ))}
            </Box>
            <Typography variant="caption" color="text.secondary">
              Click an example, or describe your own filter to Key in the chat.
            </Typography>
          </Box>
        );
      })()}

      {/* ── Results area ── (only when there are actual rows; 0-result/no-filter states show the tips above) */}
      {hasResults && criteriaRows.length > 0 && (
        <>
          {/* AI summary */}
          {criteria.summary && (
            <Paper
              variant="outlined"
              sx={{ px: 2, py: 1.25, bgcolor: 'primary.50', borderColor: 'primary.200' }}
            >
              <Typography variant="body2" color="primary.dark">
                <strong>Active filter:</strong> {criteria.summary}
              </Typography>
            </Paper>
          )}

          {/* Query engine filter chips */}
          {currentQuery && getQueryChips(currentQuery).length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, alignItems: 'center', mb: 1.5 }}>
              <FilterListIcon sx={{ fontSize: 16, color: 'text.secondary', mr: 0.5 }} />
              {getQueryChips(currentQuery).map((chip) => (
                <Chip
                  key={chip.key}
                  label={chip.label}
                  size="small"
                  color={chip.color || 'primary'}
                  variant={chip.color === 'info' ? 'filled' : 'outlined'}
                />
              ))}
              <Chip label="Clear all" size="small" variant="outlined" color="error"
                onClick={() => {
                  setCriteriaRows([]);
                  setCriteriaCount(0);
                  setCurrentQuery(null);
                  currentQueryRef.current = null;
                  setHasResults(false);
                  setOriginalPrompt('');
                }} />
            </Box>
          )}

          {/* Legacy criteria chips (criteria filter mode) */}
          {!currentQuery && <CriteriaChips
            criteria={criteria}
            onRemoveCounty={handleRemoveCounty}
            onRemoveState={handleRemoveState}
            onRemoveCity={handleRemoveCity}
            onRemoveKeyword={handleRemoveKeyword}
            onClearAll={handleClearAll}
          />}

          {/* Over-cap warning — backend returns/saves at most MAX_LABEL_RESULTS */}
          {criteriaCount > MAX_LABEL_RESULTS && !(isInterpreting || isCriteriaFetching) && (
            <Alert severity="warning" sx={{ flexShrink: 0 }}>
              This filter matches {criteriaCount.toLocaleString()}{' '}
              {aiLabelFilter?.entityType === 'organizations' ? 'organizations' : 'providers'}, but only the
              first {MAX_LABEL_RESULTS.toLocaleString()} are shown and will be saved. Narrow the filter to
              capture the rest.
            </Alert>
          )}

          {/* Provider count */}
          {(() => {
            const entityWord = aiLabelFilter?.entityType === 'organizations' ? 'organization' : 'provider';
            const topN = currentQuery?.topN ? Number(currentQuery.topN) : null;
            // When a "top N" cap is active and smaller than the full match count, only N will be saved.
            // Also hard-cap at MAX_LABEL_RESULTS — the backend never saves more than that in one pass.
            const saveCount = Math.min((topN && topN < criteriaCount) ? topN : criteriaCount, MAX_LABEL_RESULTS);
            return (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">
                  {!(isInterpreting || isCriteriaFetching) && (
                    (topN && topN < criteriaCount)
                      ? `${criteriaCount.toLocaleString()} ${entityWord}${criteriaCount !== 1 ? 's' : ''} match — saving first ${topN.toLocaleString()}`
                      : `${criteriaCount.toLocaleString()} ${entityWord}${criteriaCount !== 1 ? 's' : ''} match`
                  )}
                </Typography>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<LabelIcon />}
                  onClick={handleQuickSave}
                  disabled={criteriaRows.length === 0 || isInterpreting || isCriteriaFetching}
                >
                  {aiLabelFilter?.tagId
                    ? `Add ${saveCount.toLocaleString()} to "${aiLabelFilter.tagName || 'label'}"`
                    : 'Save'}
                </Button>
              </Box>
            );
          })()}

          {/* Provider table — minHeight:0 lets this flex item shrink below its content
               height so the table scrolls internally and the pagination/footer below
               stay visible on short screens (otherwise the default min-height:auto pushes
               the "X of N" pagination past the dialog's fixed height and clips it). */}
          <Box sx={{ position: 'relative', flexGrow: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            {isCriteriaFetching && (
              <Box sx={{
                position: 'absolute', inset: 0, zIndex: 10, borderRadius: 1,
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                backgroundColor: 'rgba(255,255,255,0.82)',
                gap: 1.5,
              }}>
                <CircularProgress size={40} thickness={3.5} />
                <Typography variant="body2" color="text.secondary" fontWeight={500}>
                  Updating results…
                </Typography>
              </Box>
            )}
          <TableContainer component={Paper} variant="outlined" sx={{ flexGrow: 1, overflow: 'auto' }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {aiLabelFilter?.entityType !== 'organizations' && (
                    <TableCell><strong>Name</strong></TableCell>
                  )}
                  {(columns || []).map(col => (
                    <TableCell key={col.key}><strong>{col.label}</strong></TableCell>
                  ))}
                  <TableCell align="right"><strong>Exclude</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isInterpreting ? (
                  <TableRow>
                    <TableCell colSpan={2 + (columns?.length || 0)} align="center" sx={{ py: 4 }}>
                      <CircularProgress size={28} />
                    </TableCell>
                  </TableRow>
                ) : criteriaRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={2 + (columns?.length || 0)} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                      No providers match the current filters.
                    </TableCell>
                  </TableRow>
                ) : (
                  criteriaRows.map((p) => (
                    <ProviderRow key={p.id} provider={p} onExclude={handleExclude} columns={columns} entityType={aiLabelFilter?.entityType} />
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          </Box>

          {criteriaCount > PAGE_SIZE && (
            <TablePagination component="div" count={criteriaCount} page={criteriaPage}
              rowsPerPage={PAGE_SIZE} rowsPerPageOptions={[PAGE_SIZE]}
              sx={{ flexShrink: 0 }}
              onPageChange={(_, p) => {
                const q = currentQueryRef.current || currentQuery;
                if (q) callBuildAndExecute(null, null, q, p);
              }} />
          )}
          <Divider sx={{ flexShrink: 0 }} />
          <Box sx={{ px: 2, py: 1, flexShrink: 0 }}>
            <Typography variant="caption" color="text.secondary">
              Use the AI assistant to refine this filter — speak or type in the chat.
            </Typography>
          </Box>
        </>
      )}

      {/* ── Save Dialog ── */}

    </Box>
  );
}

AIProviderFilter.propTypes = {
  allProviders:       PropTypes.arrayOf(PropTypes.shape({
    id:         PropTypes.number.isRequired,
    first_name: PropTypes.string,
    last_name:  PropTypes.string,
    specialty:  PropTypes.string,
    county:     PropTypes.string,
    state:      PropTypes.string,
    npi:        PropTypes.number,
  })).isRequired,
  specialtyList:       PropTypes.arrayOf(PropTypes.string).isRequired,
  existingNamespaces:  PropTypes.arrayOf(PropTypes.shape({
    id:   PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    tags: PropTypes.arrayOf(PropTypes.shape({
      id:   PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
    })),
  })).isRequired,
  onClose:        PropTypes.func,
  selectedClient: PropTypes.number,
  user:           PropTypes.object,
  router:         PropTypes.object,
};

AIProviderFilter.defaultProps = {
  existingNamespaces: [],
};

const mapStateToProps = (state) => ({
  selectedClient: state.app.selectedClient,
  user:           state.user,
  aiLabelFilter:  state.app.aiLabelFilter,
});

export default connect(mapStateToProps)(withRouter(AIProviderFilter));